import { Injectable, OnDestroy } from '@angular/core';
import { CityAccommodations } from '@app/utils/models/itinerary/city-accommodations';
import { Day } from '@app/utils/models/itinerary/day.model';
import { RouteDetails } from '@app/utils/models/itinerary/route-details.model';
import { Trip } from '@app/utils/models/itinerary/trip.model';
import { ItineraryState } from '@app/utils/models/store/itinerary-state.model';
import { LoadingState } from '@app/utils/models/store/loading-state.model';
import { TravelState } from '@app/utils/models/store/travel-state.model';
import { StaticText } from '@app/utils/models/static-text.model';
import { BehaviorSubject, distinctUntilChanged, map, Observable, shareReplay, Subject } from 'rxjs';
import { ItinerarySelectorsService } from './selectors/itinerary-selectors.service';
import { LOADING_MESSAGES } from '@app/utils/models/loading-messages';
import { Experience } from '@app/utils/models/itinerary/experience.model';

const initialState: Readonly<TravelState> = {
  itineraryState: {
    activeDay: null,
    days: [],
    isAmex: false,
    isSampleTrip: false,
    routeDetails: null,
    staticTexts: null,
    trip: null,
  },
  loadingState: {
    isLoading: false,
    screenColor: 'black',
    messageKey: LOADING_MESSAGES.default,
  },
};

@Injectable({
  providedIn: 'root',
})
export class StoreConfigService implements OnDestroy {
  days$: Observable<Day[]>;
  isSampleTrip$: Observable<boolean>;
  routeDetails$: Observable<RouteDetails | null>;
  trip$: Observable<Trip | null>;
  tripHash$: Observable<string | null>;
  isAmex$: Observable<boolean | null>;
  activeDay$: Observable<number | null>;
  staticTexts$: Observable<StaticText | null>;
  accommodationsByCity$: Observable<readonly CityAccommodations[]>;
  loadingState$: Observable<LoadingState>;
  isLoading$: Observable<boolean>;
  isTripLoaded$: Observable<boolean>;

  protected previousState: ItineraryState | null = null;
  protected store$: BehaviorSubject<TravelState>;
  private state$: Observable<TravelState>;
  private destroy$ = new Subject<void>();

  constructor(private itinerarySelectorService: ItinerarySelectorsService) {
    this.store$ = new BehaviorSubject<TravelState>(initialState);
    this.state$ = this.store$.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.days$ = this.state$.pipe(
      map(state => state.itineraryState.days),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.isSampleTrip$ = this.state$.pipe(
      map(state => state.itineraryState.isSampleTrip),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.routeDetails$ = this.state$.pipe(
      map(state => state.itineraryState.routeDetails),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.trip$ = this.state$.pipe(
      map(state => state.itineraryState.trip),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.tripHash$ = this.state$.pipe(
      map(state => state.itineraryState.trip?.hash ?? null),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.isAmex$ = this.state$.pipe(
      map(state => state.itineraryState.isAmex),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.activeDay$ = this.state$.pipe(
      map(state => state.itineraryState.activeDay),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.staticTexts$ = this.state$.pipe(
      map(state => state.itineraryState.staticTexts),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.loadingState$ = this.state$.pipe(
      map(state => state.loadingState),
      distinctUntilChanged(),
    );
    this.isLoading$ = this.loadingState$.pipe(
      map(loadingState => loadingState.isLoading),
      distinctUntilChanged(),
    );
    this.isTripLoaded$ = this.days$.pipe(
      map(days => days.length > 0),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.accommodationsByCity$ = this.itinerarySelectorService.getAccommodationsByCity$(this.days$);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getAlternativeExperience$(alternativeExperienceID: number): Observable<Experience> {
    return this.itinerarySelectorService.getAlternativeExperience$(this.days$, alternativeExperienceID);
  }

  protected setItineraryState(newState: Partial<ItineraryState>): void {
    const currentState = this.store$.getValue();
    this.previousState = currentState.itineraryState;
    this.store$.next({ ...currentState, itineraryState: { ...currentState.itineraryState, ...newState } });
  }
  protected setLoadingState(newState: Partial<LoadingState>): void {
    const currentState = this.store$.getValue();
    this.store$.next({ ...currentState, loadingState: { ...currentState.loadingState, ...newState } });
  }
  protected setInitialItineraryState(): void {
    const currentState = this.store$.getValue();
    this.setItineraryState({ ...initialState.itineraryState, routeDetails: currentState.itineraryState.routeDetails });
  }
}
